import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { BaseLayout } from './BaseLayout';
import { CERTIFICATE_LINKING_STEPS } from '../petDemographics.constants';
import { LandingScreen } from './LandingScreen';
import { UnknownStep } from './UnknownStep';
import { LoginAlreadyRegister } from './LoginAlreadyRegister';
import { VerifyScreen } from './VerifyScreen';
import { VerifyDateOfHireScreen } from './VerifyDateOfHireScreen';
import { SelectEmailScreen } from './SelectEmailScreen';
import { ConfirmRegisteredEmailScreen } from './ConfirmRegisteredEmailScreen';
import { GenericEmailSuccessStep } from './GenericEmailSuccessStep';
import { EnterAuthorizationCode } from './EnterAuthorizationCode';
import { LoginMobile } from '../../login/LoginMobile';

import { useCertificateLinkingScreen } from './hooks';
import { useResponsive } from '../../../hooks/useResponsive';

const CertificateLinkingScreen = () => {
  const { t } = useTranslation('petDemos');
  const { isMobile } = useResponsive();
  const {
    certificateNumber,
    certificates,
    currentStep,
    email,
    handleConfirmRegisteredEmail,
    handleGoBack,
    handleSelectEmailContinue,
    handleVerifyCustomer,
    onSignInClick,
    registeredEmail,
    setCertificateNumber,
    setCurrentStep,
    setEmail,
    setRegisteredEmail,
    showNoMatchError,
    showSkipButton,
    validateEmail,
    validatingEmail,
    verifyingCustomer,
  } = useCertificateLinkingScreen();

  const renderContent = useCallback(() => {
    const emailUpdatedDescKey =
      'certificateLinking.emailUpdatedSuccess.description';

    const emailUpdatedContinueText =
      t('certificateLinking.emailUpdatedSuccess.continue');

    switch (currentStep) {
      case CERTIFICATE_LINKING_STEPS.LANDING:
        return (
          <LandingScreen
            email={email}
            onSignInClick={onSignInClick}
            setEmail={setEmail}
            validateEmail={validateEmail}
            validatingEmail={validatingEmail}
          />
        );

      case CERTIFICATE_LINKING_STEPS.ALREADY_REGISTERED:
        return <LoginAlreadyRegister onSignInClick={onSignInClick} />;

      case CERTIFICATE_LINKING_STEPS.VERIFY_DETAILS:
        return (
          <VerifyScreen
            email={email}
            onVerify={handleVerifyCustomer}
            setCurrentStep={setCurrentStep}
            showNoMatchError={showNoMatchError}
            showSkipButton={showSkipButton}
            verifyingCustomer={verifyingCustomer}
          />
        );

      case CERTIFICATE_LINKING_STEPS.VERIFY_DATE_OF_HIRE:
        return (
          <VerifyDateOfHireScreen
            certificates={certificates}
            email={email}
            setCertificateNumber={setCertificateNumber}
            setCurrentStep={setCurrentStep}
            setRegisteredEmail={setRegisteredEmail}
          />
        );

      case CERTIFICATE_LINKING_STEPS.SELECT_EMAIL:
        return (
          <SelectEmailScreen
            newEmail={email}
            onContinue={handleSelectEmailContinue}
            registeredEmail={registeredEmail}
          />
        );

      case CERTIFICATE_LINKING_STEPS.EMAIL_LINKED_SUCCESS:
        return (
          <GenericEmailSuccessStep
            email={email}
            onContinuePress={onSignInClick}
          />
        );

      case CERTIFICATE_LINKING_STEPS.CONFIRM_REGISTERED_EMAIL:
        return (
          <ConfirmRegisteredEmailScreen
            onConfirm={handleConfirmRegisteredEmail}
            registeredEmail={registeredEmail}
          />
        );

      case CERTIFICATE_LINKING_STEPS.ENTER_AUTHORIZATION_CODE:
        return (
          <EnterAuthorizationCode
            certificateNumber={certificateNumber}
            email={email}
            setCurrentStep={setCurrentStep}
          />
        );

      case CERTIFICATE_LINKING_STEPS.EMAIL_UPDATED_SUCCESS:
        return (
          <GenericEmailSuccessStep
            customContinueButtonText={emailUpdatedContinueText}
            customI18nKeyDescription={emailUpdatedDescKey}
            email={email}
            onContinuePress={onSignInClick}
          />
        );

      default:
        return <UnknownStep step={currentStep} />;
    }
  }, [
    certificateNumber,
    certificates,
    currentStep,
    email,
    handleConfirmRegisteredEmail,
    handleSelectEmailContinue,
    handleVerifyCustomer,
    onSignInClick,
    registeredEmail,
    setCertificateNumber,
    setCurrentStep,
    setEmail,
    setRegisteredEmail,
    showNoMatchError,
    showSkipButton,
    validateEmail,
    validatingEmail,
    verifyingCustomer,
  ]);

  const isEmailUpdatedSuccess =
    currentStep === CERTIFICATE_LINKING_STEPS.EMAIL_UPDATED_SUCCESS;

  if (isMobile && isEmailUpdatedSuccess) {
    return <LoginMobile />;
  }

  return (
    <BaseLayout
      currentStep={currentStep}
      handleGoBack={handleGoBack}
    >
      {renderContent()}
    </BaseLayout>
  );
};

export { CertificateLinkingScreen };
